import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useCopyLineSheetSet, {
  CopyLineSheetSetResponse,
} from "@/features/line-sheet-sets/hooks/use-copy-line-sheet-set";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { Company } from "@/features/types";

export interface CopyLineSheetSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineSheetSetId: number;
  lineSheetSetName?: string;
  mutate?: () => void;
  onCopied?: (response: CopyLineSheetSetResponse) => void;
}

export default function CopyLineSheetSetModal({
  isOpen,
  onClose,
  lineSheetSetId,
  lineSheetSetName,
  mutate,
  onCopied,
}: CopyLineSheetSetModalProps) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const toasts = useAppToasts();
  const [boutique, setBoutique] = useState<Company | undefined>();
  const { fire, isLoading } = useCopyLineSheetSet();

  const handleCopy = () => {
    if (!identity?.company || !boutique || isLoading) {
      return;
    }
    fire(
      {
        by: identity.company.type,
        companyId: identity.company.id,
        lineSheetSetId,
      },
      { issuedByBoutique: { id: boutique.id } },
      { rethrow: true }
    )
      .then((response) => {
        toasts.info("라인시트세트가 복사되었습니다. 파싱이 진행됩니다.");
        mutate?.();
        onCopied?.(response);
        onClose();
      })
      .catch((e) => {
        toasts.error(e);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{tTitle("copy")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection={"column"} gap={"12px"}>
            {lineSheetSetName && (
              <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
                {lineSheetSetName}
              </Text>
            )}
            <AppCompanySelect
              name={tTitle("issued_by")}
              types={["BOUTIQUE"]}
              id={boutique?.id}
              isSearchable={true}
              width={"100%"}
              onSelect={(value) => setBoutique(value)}
            />
          </Flex>
        </ModalBody>
        <ModalFooter gap={"8px"}>
          <AppButton variant={"gray"} onClick={onClose}>
            {tTitle("cancel")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={!boutique}
            isLoading={isLoading}
            onClick={handleCopy}
          >
            {tTitle("copy")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
